/**
 * moduleWhatsNewLogic — PURE helpers behind the modular (per-module) "What's New" of the app.
 *
 * No React, no AsyncStorage, no navigation: inputs come in as arguments so the matching, seen and
 * merge rules are unit-testable under plain Node (see app/tests/moduleWhatsNewLogic.test.js).
 * The stateful binding (registry + AsyncStorage + events) lives in `moduleWhatsNewStore.js`;
 * the authoring format is documented in `README.md`.
 *
 * Entry contract:
 *   { id, module, routes: (string|{screen, params?})[], date, version?, global?, type?,
 *     title, body, maxAgeDays?, expiresAt?, audience? }
 */

/** Entries older than this many days are never shown as a modal (the global summary is unaffected). */
export const DEFAULT_MAX_AGE_DAYS = 60;

/** AsyncStorage key prefix of the per-user seen map: `whatsNewModuleSeen:<userId>`. */
export const SEEN_KEY_PREFIX = 'whatsNewModuleSeen';

/** Group types an entry may use (mirrors GROUP_META in WhatsNewModal). */
export const ENTRY_TYPES = ['feature', 'improvement', 'fix', 'security', 'performance', 'other', 'internal'];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * AsyncStorage key holding one user's seen map.
 * @param {string} userId Stable user id.
 * @returns {string}
 */
export const getSeenKey = (userId) => `${SEEN_KEY_PREFIX}:${String(userId || '')}`;

/**
 * Parse a date-like value into epoch ms.
 * @param {string|number|Date} value Date-like value.
 * @returns {number} Epoch ms, NaN when unparsable.
 */
const toTime = (value) => {
  if (value === undefined || value === null || value === '') {
    return NaN;
  }

  return value instanceof Date ? value.getTime() : new Date(value).getTime();
};

/**
 * Match one `routes` item against the focused route.
 *  - a string is a screen name (`'ManagementListScreen'`);
 *  - `{ screen, params }` also requires every listed param to equal the route's param
 *    (`{ screen: 'ManagementDetailScreen', params: { type: 'leads' } }`), for the shared
 *    management screens that serve many record types.
 *
 * @param {string|{screen: string, params?: Object}} spec Entry route spec.
 * @param {{name?: string, params?: Object}|null} route Focused route (`navigationRef.getCurrentRoute()`).
 * @returns {boolean}
 */
export const matchRouteSpec = (spec, route) => {
  if (!route || !route.name) {
    return false;
  }
  if (typeof spec === 'string') {
    return spec === route.name;
  }
  if (!spec || typeof spec !== 'object' || spec.screen !== route.name) {
    return false;
  }
  if (!spec.params) {
    return true;
  }
  const have = route.params || {};

  return Object.keys(spec.params).every((key) => have[key] === spec.params[key]);
};

/**
 * Whether an entry is attached to the focused route.
 * @param {Object} entry Module entry.
 * @param {{name?: string, params?: Object}|null} route Focused route.
 * @returns {boolean}
 */
export const entryMatchesRoute = (entry, route) => Array.isArray(entry?.routes)
  && entry.routes.some((spec) => matchRouteSpec(spec, route));

/**
 * Whether an entry may still be surfaced at `now`: not past `expiresAt` and younger than its
 * `maxAgeDays` (default 60; `0` disables the age limit). No parsable `date` => never live.
 *
 * @param {Object} entry Module entry.
 * @param {Date|number} [now] Reference time.
 * @param {Object} [options]
 * @param {number} [options.defaultMaxAgeDays] Fallback age window.
 * @returns {boolean}
 */
export const isEntryLive = (entry, now = new Date(), { defaultMaxAgeDays = DEFAULT_MAX_AGE_DAYS } = {}) => {
  const nowMs = toTime(now);
  const dateMs = toTime(entry?.date);

  if (Number.isNaN(nowMs) || Number.isNaN(dateMs)) {
    return false;
  }
  if (entry.expiresAt) {
    const expiresMs = toTime(entry.expiresAt);

    if (!Number.isNaN(expiresMs) && nowMs > expiresMs) {
      return false;
    }
  }
  const maxAge = Number.isFinite(entry.maxAgeDays) ? entry.maxAgeDays : defaultMaxAgeDays;

  return maxAge <= 0 || nowMs - dateMs <= maxAge * DAY_MS;
};

/**
 * Baseline for brand-new users: an entry that shipped before the account existed is not shown.
 * Unknown account date => no baseline.
 *
 * @param {Object} entry Module entry.
 * @param {string|number|Date} [userCreatedAt] Account creation time.
 * @returns {boolean}
 */
export const isEntryNewForUser = (entry, userCreatedAt) => {
  const createdMs = toTime(userCreatedAt);
  const dateMs = toTime(entry?.date);

  if (Number.isNaN(createdMs) || Number.isNaN(dateMs)) {
    return true;
  }

  // Whole-day comparison: an entry dated "2026-10-13" is new for an account created that afternoon.
  return dateMs + DAY_MS > createdMs;
};

/**
 * Defensively parse the stored seen state.
 * @param {*} raw Whatever storage returned (object, JSON string, null, garbage).
 * @returns {{ v: number, seen: Object<string, string> }}
 */
export const parseSeenState = (raw) => {
  let value = raw;

  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (_e) {
      value = null;
    }
  }
  const seen = value && typeof value === 'object' && value.seen && typeof value.seen === 'object' && !Array.isArray(value.seen)
    ? value.seen
    : {};

  return { v: 1, seen: { ...seen } };
};

/**
 * New seen state with `ids` stamped as seen at `now`. Already-seen ids keep their first timestamp.
 * @param {{ seen: Object<string, string> }} state Current state.
 * @param {string[]} ids Entry ids to mark.
 * @param {Date|number} [now] Reference time.
 * @returns {{ v: number, seen: Object<string, string> }}
 */
export const markEntriesSeen = (state, ids, now = new Date()) => {
  const next = parseSeenState(state);
  const stamp = new Date(toTime(now) || Date.now()).toISOString();

  (Array.isArray(ids) ? ids : []).forEach((id) => {
    if (id && !next.seen[id]) {
      next.seen[id] = stamp;
    }
  });

  return next;
};

/**
 * Entries the user should be shown now, newest first.
 *
 * @param {Object[]} entries Registry entries.
 * @param {Object} query
 * @param {{name?: string, params?: Object}} [query.route] Match by focused route (watcher).
 * @param {string} [query.module] Match by module key (hook for views with no distinct screen).
 * @param {{ seen: Object<string, string> }} [query.seenState] Per-user seen state.
 * @param {Date|number} [query.now] Reference time.
 * @param {string|number|Date} [query.userCreatedAt] Account creation (baseline for new users).
 * @param {boolean} [query.includeInternal] Viewer is system:admin (`audience: 'internal'` entries).
 * @returns {Object[]}
 */
export const getUnseenEntries = (entries, {
  route, module, seenState, now = new Date(), userCreatedAt, includeInternal = false,
} = {}) => {
  const seen = parseSeenState(seenState).seen;

  return (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry && entry.id && !seen[entry.id])
    .filter((entry) => includeInternal || String(entry.audience || '').toLowerCase() !== 'internal')
    .filter((entry) => (module ? entry.module === module : entryMatchesRoute(entry, route)))
    .filter((entry) => isEntryLive(entry, now))
    .filter((entry) => isEntryNewForUser(entry, userCreatedAt))
    .sort((a, b) => toTime(b.date) - toTime(a.date));
};

/**
 * Authoring lint for the registry: human-readable problems (empty array = valid). Run by the unit
 * test over the shipped registry so a typo cannot silently hide an entry.
 *
 * @param {Object} entry Module entry.
 * @param {string[]} [locales] Locale codes the title/body must all provide.
 * @returns {string[]}
 */
export const validateEntry = (entry, locales = []) => {
  const problems = [];

  if (!entry || typeof entry !== 'object') {
    return ['entry is not an object'];
  }
  if (!entry.id || typeof entry.id !== 'string') {
    problems.push('id is required');
  }
  if (!entry.module || typeof entry.module !== 'string') {
    problems.push('module is required');
  }
  const routeOk = (spec) => (typeof spec === 'string' && spec.length > 0)
    || (spec && typeof spec === 'object' && typeof spec.screen === 'string' && spec.screen.length > 0);

  if (!Array.isArray(entry.routes) || entry.routes.length === 0 || !entry.routes.every(routeOk)) {
    problems.push('routes must be screen names or { screen, params } objects');
  }
  if (Number.isNaN(toTime(entry.date))) {
    problems.push('date must be a valid ISO date');
  }
  if (entry.version && !/^\d+\.\d+\.\d+/.test(entry.version)) {
    problems.push('version must look like 4.15.0');
  }
  if (entry.type && !ENTRY_TYPES.includes(entry.type)) {
    problems.push(`type must be one of ${ENTRY_TYPES.join(', ')}`);
  }
  ['title', 'body'].forEach((field) => {
    locales.forEach((code) => {
      if (!entry[field] || !entry[field][code]) {
        problems.push(`${field}.${code} is missing`);
      }
    });
  });

  return problems;
};

/**
 * Numeric semver-ish comparator, newest first (`a` newer => negative).
 * @param {string} a Version.
 * @param {string} b Version.
 * @returns {number}
 */
export const compareVersionsDesc = (a, b) => {
  const pa = String(a || '0').split('+')[0].split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('+')[0].split('.').map((n) => parseInt(n, 10) || 0);

  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) !== (pb[i] || 0)) {
      return (pb[i] || 0) - (pa[i] || 0);
    }
  }

  return 0;
};

/**
 * Merge `global: true` entries into the global changelog releases, additively (inputs not mutated).
 *  - the entry joins the release matching its `version` (a release is created when none exists);
 *  - an entry without a version joins the newest release;
 *  - it becomes an item of the group matching its `type` (default `feature`);
 *  - idempotent: an entry already merged (same `moduleEntryId`) is skipped.
 *
 * @param {Object[]} releases Changelog releases (any order).
 * @param {Object[]} entries Registry entries.
 * @returns {Object[]} Releases sorted newest first.
 */
export const mergeGlobalEntriesIntoReleases = (releases, entries) => {
  const sorted = [...(Array.isArray(releases) ? releases : [])]
    .sort((a, b) => compareVersionsDesc(a?.version, b?.version));
  const globals = (Array.isArray(entries) ? entries : []).filter((entry) => entry && entry.global === true && entry.id);

  if (globals.length === 0) {
    return sorted;
  }
  const result = sorted.map((release) => ({
    ...release,
    groups: (Array.isArray(release.groups) ? release.groups : []).map((group) => ({
      ...group,
      items: [...(Array.isArray(group.items) ? group.items : [])],
    })),
  }));

  globals.forEach((entry) => {
    const targetVersion = entry.version || result[0]?.version;

    if (!targetVersion) {
      return;
    }
    let release = result.find((r) => r.version === targetVersion);

    if (!release) {
      release = { version: targetVersion, date: String(entry.date || '').slice(0, 10), groups: [] };
      result.push(release);
    }
    const type = ENTRY_TYPES.includes(entry.type) ? entry.type : 'feature';

    if (release.groups.some((group) => group.items.some((item) => item.moduleEntryId === entry.id))) {
      return;
    }
    let group = release.groups.find((g) => g.type === type);

    if (!group) {
      group = { type, items: [] };
      release.groups.push(group);
    }
    const item = { title: entry.title, description: entry.body, moduleEntryId: entry.id };

    if (entry.audience) {
      item.audience = entry.audience;
    }
    group.items.push(item);
  });

  return result.sort((a, b) => compareVersionsDesc(a.version, b.version));
};
